const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/soh/eCheckin/sections/PersAngaben.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import textMap from '../translations';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import textMapCommon from '../../../common/translations';
import { AddressBlock } from 'App/components/eCheckin/components/fields/AddressBlock';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { CountryField } from 'App/components/eCheckin/components/fields/metakat/CountryField';
import { atLeastOneHasValue } from '@cistec/common_web/modules/CIS4m/utils/data';
import { GenderField } from 'App/components/eCheckin/components/fields/metakat/GenderField';
import { ZivilstandField } from 'App/components/eCheckin/components/fields/metakat/ZivilstandField';
import { PhoneNumberField } from 'App/components/eCheckin/components/fields/PhoneNumberField';
import { EMailField } from 'App/components/eCheckin/components/fields/EMailField';
import { BirthDateField } from 'App/components/eCheckin/components/fields/BirthDateField';
import { ReligionField } from 'App/components/eCheckin/components/fields/metakat/ReligionField';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import { LanguageField } from 'App/components/eCheckin/components/fields/metakat/LanguageField';
import { createFieldPath, fieldValueIsNot } from 'App/components/eCheckin/utils';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { useIntl } from 'react-intl';

export const PersAngaben = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);
    const { formatMessage } = useIntl();

    const PATH_FIRST_NAME = createFieldPath(path, `firstname`);
    const PATH_LAST_NAME = createFieldPath(path, `lastname`);
    const PATH_MAIDEN_NAME = createFieldPath(path, `maidenname`);
    const PATH_DATE_OF_BIRTH = createFieldPath(path, `dateOfBirth`);
    const PATH_RELIGION = createFieldPath(path, `religion`);
    const PATH_COUNTRY = createFieldPath(path, `country`);
    const PATH_EMAIL = createFieldPath(path, `email`);
    const PATH_TEL = createFieldPath(path, `tel`);
    const PATH_MOBILE = createFieldPath(path, `mobile`);
    const PATH_GENDER = createFieldPath(path, `gender`);
    const PATH_NATIONALITY = createFieldPath(path, `nationality`);
    const PATH_LANGUAGE = createFieldPath(path, `language`);
    const PATH_ZIVILSTAND = createFieldPath(path, `zivilstand`);
    const PATH_PROFESSION = createFieldPath(path, `profession`);
    const PATH_PFARRAMT = createFieldPath(path, `pfarramt`);
    const PATH_ZIVIL_WOHNSITZ = createFieldPath(path, `zivilWohnsitz`);

    const eCheckinSectionRef = useCIS4m(ref);
    const formData = useCIS4mData();

    const phoneNumberRequired = !atLeastOneHasValue(formData, [PATH_TEL, PATH_MOBILE]);
    const hideZivilWohnsitz = !fieldValueIsNot(formData, PATH_ZIVIL_WOHNSITZ, ['1']);

    const zivilWohnsitzAddressFieldsConfig = {
        streetAndNumber: {
            path: 'zivilWohnStreetAndNumber',
        },
        adresszusatz: {
            path: 'zivilWohnAdressZusatz',
        },
        country: {
            path: 'zivilWohnCountry',
        },
        postalCode: {
            path: 'zivilWohnPlz',
        },
        location: {
            path: 'zivilWohnOrt',
        },
    };

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <p>{formatMessage(textMap.infoText)}</p>
            <CIS4mTextField label={textMapCommon.lastname} path={PATH_LAST_NAME} required={true} />
            <CIS4mTextField label={textMapCommon.firstname} path={PATH_FIRST_NAME} required={true} />
            <CIS4mTextField label={textMapCommon.maidenname} path={PATH_MAIDEN_NAME} />
            <GenderField label={textMap.gender} path={PATH_GENDER} required={true} />
            <BirthDateField label={textMap.dateOfBirth} path={PATH_DATE_OF_BIRTH} required={true} />
            <ZivilstandField label={textMap.zivilstand} path={PATH_ZIVILSTAND} required={true} />
            <CountryField label={textMap.nationality} path={PATH_NATIONALITY} required={true} />
            <CIS4mTextField label={textMap.profession} path={PATH_PROFESSION} />
            <LanguageField label={textMap.requestedLanguage} path={PATH_LANGUAGE} required={true} />
            <ReligionField label={textMap.religion} path={PATH_RELIGION} required={true} />
            <YesNoField label={textMap.pfarramt} path={PATH_PFARRAMT} required={true} />
            <AddressBlock basePath={path} required={true} />
            <PhoneNumberField
                label={textMapCommon.mobile}
                path={PATH_MOBILE}
                required={phoneNumberRequired}
                countryCodePath={PATH_COUNTRY}
            />
            <PhoneNumberField
                label={textMapCommon.tel}
                path={PATH_TEL}
                required={phoneNumberRequired}
                countryCodePath={PATH_COUNTRY}
            />
            <EMailField label={textMapCommon.email} path={PATH_EMAIL} />
            <YesNoField label={textMap.zivilWohnsitz} path={PATH_ZIVIL_WOHNSITZ} required={true} />
            <AddressBlock
                basePath={path}
                required={true}
                fieldsConfig={zivilWohnsitzAddressFieldsConfig}
                hidden={!hideZivilWohnsitz}
            />
        </ECheckinSection>
    );
});

PersAngaben.displayName = 'PersAngaben';

export default PersAngaben;
